/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable max-statements-per-line */
/* eslint-disable react/prop-types */
// src/twilio/TwilioProvider.tsx
import { fetchIntegrationsThunk } from '../redux/thunks/integration';
import { fetchTwilioTokenThunk } from '../redux/thunks/sms';
import IntegrationApi from '../apis/integration';
import { IntegrationType } from '../params/redux/integration';
import { useParams } from 'react-router';
import { AppDispatch, RootState } from '../redux/store';
import { Call, Device } from '@twilio/voice-sdk';
import React, { createContext, useContext, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

interface TwilioCallLogResponse extends Record<string, unknown> {
    id: number;
    call_sid: string;
    twilio_from: string;
    twilio_to: string;
    type: string;
    contact_name: string;
    deal_name: string;
    created_at: string;
    updated_at: string;
}

type TwilioContextType = {
    incomingCall: Call | null;
    activeCall: Call | null;
    callStatus: string;
    callDuration: number;
    accept: () => void;
    reject: () => void;
    hangup: () => void;
    user: TwilioCallLogResponse;
};


const TwilioContext = createContext<TwilioContextType | null>(null);

export const TwilioProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const dispatch = useDispatch<AppDispatch>();
    const { id } = useParams();
    const { token } = useSelector((state: RootState) => state.app);
    const deviceRef = useRef<Device | null>(null);
    const [user, setUser] = useState<TwilioCallLogResponse>({
        id: 0,
        call_sid: '',
        twilio_from: '',
        twilio_to: '',
        type: '',
        contact_name: '',
        deal_name: '',
        created_at: '',
        updated_at: ''
    });
    const [incomingCall, setIncomingCall] = useState<Call | null>(null);
    const [activeCall, setActiveCall] = useState<Call | null>(null);
    const [callStatus, setCallStatus] = useState('');
    const [, setCallStart] = useState<number>(0);
    const [callDuration, setCallDuration] = useState(0);
    const timerRef = useRef<NodeJS.Timeout | null>(null);

    useEffect(() => {
        let mounted = true;
        const createCall = async () => {
            const integrationResponse: IntegrationType[] = await dispatch(fetchIntegrationsThunk()).unwrap() as any;
            if (!integrationResponse?.includes('twilio')) return;
            // 1️⃣ fetch a fresh Twilio Voice token
            const res = await dispatch(fetchTwilioTokenThunk(id ? parseInt(id as string, 10) : 0)).unwrap() as string;
            // 2️⃣ instantiate & register the Device once
            const dev = new Device(res, { });
            deviceRef.current = dev;
            dev.on('registered', () => console.log('Twilio Device registered'));
            dev.on('error', (err) => console.error('Device error', err));
            // 3️⃣ listen for inbound calls
            dev.on('incoming', async (call: Call) => {
                console.log('Incoming call', call);
                if (!mounted) { call.reject(); return; }
                setIncomingCall(call);
                const response = await IntegrationApi.twilioIncomingCallLog(token, call.parameters.To, call.parameters.From);
                setUser(response.data as TwilioCallLogResponse);

                 call.on('cancel', () => {
                    setIncomingCall(null);
                    setCallStatus('Canceled (answered elsewhere)');
                });
            });
            dev.register();
        };
        createCall();

        return () => {
            mounted = false;
            deviceRef.current?.removeAllListeners();
            deviceRef.current?.destroy();
            if (timerRef.current) clearInterval(timerRef.current);
        };
    }, [dispatch]);

    const accept = () => {
        if (!incomingCall) return;
        incomingCall.accept();
        setActiveCall(incomingCall);
        setIncomingCall(null);
        setCallStatus('Call connected');
        setCallStart(Date.now());
        // wire up hangup and disconnect
        incomingCall.on('disconnect', () => {
            setCallStatus('Call ended');
            setActiveCall(null);
            if (timerRef.current) clearInterval(timerRef.current);
            setCallDuration(0);
        });
        timerRef.current = setInterval(() => {
                    setCallDuration((prev) => prev + 1);
                }, 1000);
    };
    const reject = () => {
        if (!incomingCall) return;
        incomingCall.reject();
        setIncomingCall(null);
        setCallStatus('Call rejected');
    };

    const hangup = () => {
        if (!activeCall) return;
        activeCall.disconnect();
        // setActiveCall(null);
        // setCallStatus('Call ended');
        // if (timerRef.current) clearInterval(timerRef.current);
        // setCallDuration(0);
    };


    return (
        <TwilioContext.Provider value={{
            incomingCall,
            activeCall,
            callStatus,
            callDuration,
            accept,
            reject,
            hangup,
            user
        }}>
            {children}
        </TwilioContext.Provider>
    );
};

export const useTwilio = () => {
    const ctx = useContext(TwilioContext);
    if (!ctx) throw new Error('useTwilio must be inside TwilioProvider');
    return ctx;
};
